<script lang="ts">
    import type { SettingsPages } from "./create";
    import { Settings } from ".";
    import type { CommandHandler } from "..";
    import { onMount } from "svelte";
    import { linkHandling} from "./link";
    import { closeLink, type LinkCallback, linkDirectory, openLink } from "../navigation/hashLinking";

    export let pages: SettingsPages;
    export let command: {
        handler: CommandHandler,
        id: string,
        scope?: string,
        openScope?: string
    } | undefined = undefined;

    onMount(() => {
        return command?.handler.registerAction("general.settings", () => {
            open();
        }, command?.scope ?? "all");
    });


    let modal: HTMLDialogElement;

    let removeScope: (() => void) | undefined = undefined;

    let opened = false;
    let startPage: string | undefined = undefined;

    export function open() {
        if (opened) return;
        openLink("settings");
    }

    function openInternal(page?: string) {
        if (opened) return;
        linkHandling.reset();

        if (command?.openScope) {
            removeScope = command.handler.setScope(command.openScope);
        }

        startPage = page;
        opened = true;
        modal.showModal();
    }

    function close() {
        closeInternal(false);
    }

    function closeInternal(link: boolean) {
        if (!opened) return;
        !link && closeLink();

        opened = false;
        modal.close();

        removeScope?.();
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            e.stopPropagation();
            e.preventDefault();
            return;
        }
    }

    const onOpenLink: LinkCallback = ({ path }) => {
        const [_, pageId, settingId]: string[] = path;
        openInternal(pageId);

        linkHandling.open(pageId, settingId);
    }

    const onCloseLink: LinkCallback = ({ path, oldPath }) => {
        if (path[0] === oldPath[0]) return;
        closeInternal(true);
    }

</script>

<!----------------------------------------------------------------------------->

<svelte:window use:linkDirectory={{id: "settings", openCallback: onOpenLink, closeCallback: onCloseLink }} />

<dialog bind:this={modal} class:opened on:close={close} on:keydown={onKeyDown}>
    {#if opened}
        <Settings {pages} {startPage} on:close={close} />
    {/if}
</dialog>

<!----------------------------------------------------------------------------->

<style>
    dialog {
        --window-inset: 4em;
        --window-width: min(120ch, calc(100vw - 8em));
        --window-fullscreen: 0;

        all: unset;
        position: absolute;
        inset: var(--window-inset);
        margin: auto;
        display: none;


        width: max-content;
    }

    @media screen and (max-width: 120ch) {
        dialog {
            inset: 0;
            width: 100vw;
            --window-width: 100vw;
            --window-inset: 0;
            --window-fullscreen: 1;
        }
    }

    dialog::backdrop {
        background-color: #0006;
    }

    .opened {
        display: unset;
    }
</style>
